/**
 * Advanced Snippet Engine
 * Parses VS Code snippet syntax and handles tab stop navigation
 */

import { ParsedSnippet, TabStop } from './types'
import { EditorView } from '@codemirror/view'
import { setTabStops, TabStop as NavigationTabStop } from './tabStopNavigation'
import { logDebug } from '@utils/logger'

/**
 * Parse VS Code snippet syntax
 * Supports: ${1:label}, $1, $0, ${1}, etc.
 */
export function parseSnippet(snippet: string): ParsedSnippet {
  const tabStops: TabStop[] = []
  let finalPosition = -1
  let text = ''
  let i = 0
  
  while (i < snippet.length) {
    // Handle ${N:label} or ${N} format
    if (snippet[i] === '$' && i + 1 < snippet.length && snippet[i + 1] === '{') {
      const match = snippet.substring(i).match(/^\$\{(\d+)(?::([^}]+))?\}/)
      if (match) {
        const tabStop = parseInt(match[1])
        const placeholder = match[2] || ''
        
        if (tabStop === 0) {
          // Final cursor position
          finalPosition = text.length
        } else {
          // Regular tab stop
          const start = text.length
          const end = start + placeholder.length
          tabStops.push({
            index: tabStop,
            start,
            end,
            placeholder,
            isFinal: false
          })
          text += placeholder
        }
        
        i += match[0].length
        continue
      }
    }
    
    // Handle $N format (no placeholder)
    if (snippet[i] === '$' && i + 1 < snippet.length && /\d/.test(snippet[i + 1])) {
      const match = snippet.substring(i).match(/^\$(\d+)/)
      if (match) {
        const tabStop = parseInt(match[1])
        
        if (tabStop === 0) {
          finalPosition = text.length
        } else {
          // Empty tab stop
          tabStops.push({
            index: tabStop,
            start: text.length,
            end: text.length,
            isFinal: false
          })
        }
        
        i += match[0].length
        continue
      }
    }
    
    // Handle escaped characters
    if (snippet[i] === '\\' && i + 1 < snippet.length) {
      // Handle escaped backslash
      if (snippet[i + 1] === '\\') {
        text += '\\'
        i += 2
        continue
      }
      // Handle escaped dollar
      if (snippet[i + 1] === '$') {
        text += '$'
        i += 2
        continue
      }
      // Handle escaped brace
      if (snippet[i + 1] === '{' || snippet[i + 1] === '}') {
        text += snippet[i + 1]
        i += 2
        continue
      }
    }
    
    // Regular character
    text += snippet[i]
    i++
  }
  
  // Sort tab stops by index
  tabStops.sort((a, b) => a.index - b.index)
  
  // If no final position specified, use end of text
  if (finalPosition === -1) {
    finalPosition = text.length
  }
  
  logDebug('[SnippetEngine] Parsed snippet', {
    textLength: text.length,
    tabStopsCount: tabStops.length,
    finalPosition
  })
  
  return {
    text,
    tabStops,
    finalPosition
  }
}

/**
 * Insert snippet into editor at specified position
 */
export function insertSnippet(
  view: EditorView,
  from: number,
  to: number,
  snippet: ParsedSnippet
): void {
  // Calculate absolute positions for tab stops
  const absoluteTabStops: NavigationTabStop[] = snippet.tabStops.map((stop, idx) => ({
    id: `tabstop-${idx}`,
    from: from + stop.start,
    to: from + stop.end,
    placeholder: stop.placeholder,
    isFinal: stop.isFinal
  }))
  
  const finalPosition = from + snippet.finalPosition
  
  // Insert the text
  view.dispatch({
    changes: {
      from,
      to,
      insert: snippet.text
    },
    selection: {
      anchor: absoluteTabStops.length > 0 ? absoluteTabStops[0].from : finalPosition,
      head: absoluteTabStops.length > 0 ? absoluteTabStops[0].to : finalPosition
    }
  })
  
  // Set tab stops for navigation
  if (absoluteTabStops.length > 0) {
    setTabStops(view, absoluteTabStops)
  }
  
  logDebug('[SnippetEngine] Inserted snippet', {
    from,
    to,
    textLength: snippet.text.length,
    tabStops: absoluteTabStops.length
  })
}

/**
 * Insert simple text with cursor positioning
 */
export function insertTextWithCursor(
  view: EditorView,
  from: number,
  to: number,
  text: string,
  cursorPosition?: number
): void {
  const finalPos = cursorPosition !== undefined 
    ? from + cursorPosition 
    : from + text.length
    
  view.dispatch({
    changes: {
      from,
      to,
      insert: text
    },
    selection: {
      anchor: finalPos
    }
  })
}

